import type { GLToastProps } from '@group-link-one/gl-components';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

type PageLayoutContextType = {
  content: ReactElement | null;
  snackbar: GLToastProps | null;
  footerText?: string;
  /** Screen-specific controls rendered in the page header, e.g. the tenant filter. */
  headerActions: ReactNode;
  setSnackbarState: (params: GLToastProps | null) => void;
  setContentState: (newContent: ReactElement | null) => void;
  setHeaderActions: (actions: ReactNode) => void;
};

const PageLayoutContext = createContext<PageLayoutContextType | undefined>(undefined);

type PageLayoutProviderProps = {
  children: ReactNode;
  footerText?: string;
};

export const PageLayoutProvider = ({ footerText, children }: PageLayoutProviderProps) => {
  const [content, setContent] = useState<ReactElement | null>(null);
  const [snackbar, setSnackbar] = useState<GLToastProps | null>(null);
  const [headerActions, setHeaderActions] = useState<ReactNode>(null);

  function setContentState(newContent: ReactElement | null) {
    setContent(newContent);
  }

  function setSnackbarState(params: GLToastProps | null) {
    setSnackbar(params);
  }

  return (
    <PageLayoutContext.Provider
      value={{
        content,
        snackbar,
        footerText,
        headerActions,
        setContentState,
        setSnackbarState,
        setHeaderActions,
      }}
    >
      {children}
    </PageLayoutContext.Provider>
  );
};

export const usePageLayout = (): PageLayoutContextType => {
  const context = useContext(PageLayoutContext);
  if (!context) {
    throw new Error('usePageLayout must be used within a PageLayoutProvider');
  }
  return context;
};

/**
 * Publishes a screen's controls into the page header for as long as that screen is mounted.
 *
 * Clears on unmount, so navigating away never leaves the previous screen's filter behind.
 *
 * @param actions - The controls to show. Memoize them, or pass the deps that rebuild them.
 */
export const useHeaderActions = (actions: ReactNode): void => {
  const { setHeaderActions } = usePageLayout();

  useEffect(() => {
    setHeaderActions(actions);
    return () => {
      setHeaderActions(null);
    };
  }, [actions, setHeaderActions]);
};
